/**
 * Parse SE Ranking raw data into grouped issues for display
 */

import type {
  SerankingRawData,
  ParsedIssues,
  SeoIssue,
  PageSpeedRawData,
  PageSpeedOpportunity,
} from './types'
import { SECTION_NAMES } from './types'

/**
 * Parse SE Ranking raw data into structured issues
 */
export function parseSerankingIssues(rawDataStr: string | null): ParsedIssues | null {
  if (!rawDataStr) return null

  try {
    const rawData = JSON.parse(rawDataStr) as SerankingRawData
    if (!rawData.sections) return null

    const result: ParsedIssues = {
      byStatus: {
        error: [],
        warning: [],
        notice: [],
        passed: [],
      },
      bySection: {},
    }

    for (const section of rawData.sections) {
      const sectionName = SECTION_NAMES[section.uid] || section.name || section.uid
      const sectionIssues: SeoIssue[] = []
      let passedCount = 0

      for (const [code, prop] of Object.entries(section.props || {})) {
        const issue: SeoIssue = {
          code,
          name: prop.name,
          status: prop.status,
          value: prop.value,
          sectionUid: section.uid,
          sectionName,
        }

        // Add to status group (only issues, not passed checks for the main list)
        if (prop.status !== 'passed') {
          result.byStatus[prop.status].push(issue)
          sectionIssues.push(issue)
        } else {
          passedCount++
        }
      }

      // Add section if it has any checks
      if (sectionIssues.length > 0 || passedCount > 0) {
        result.bySection[section.uid] = {
          name: sectionName,
          issues: sectionIssues,
          passedCount,
        }
      }
    }

    return result
  } catch (e) {
    console.error('Failed to parse SE Ranking raw data:', e)
    return null
  }
}


/**
 * Parse PageSpeed raw data to extract improvement opportunities
 */
export function parsePageSpeedOpportunities(rawDataStr: string | null): PageSpeedOpportunity[] {
  if (!rawDataStr) return []

  try {
    const rawData = JSON.parse(rawDataStr) as PageSpeedRawData
    const audits = rawData.mobile?.rawData?.lighthouseResult?.audits

    if (!audits) return []

    const opportunities: PageSpeedOpportunity[] = []

    // Audit IDs that represent improvement opportunities
    const opportunityAudits = [
      'render-blocking-resources',
      'unused-css-rules',
      'unused-javascript',
      'modern-image-formats',
      'uses-optimized-images',
      'uses-responsive-images',
      'offscreen-images',
      'unminified-css',
      'unminified-javascript',
      'efficient-animated-content',
      'duplicated-javascript',
      'legacy-javascript',
      'uses-text-compression',
      'uses-rel-preconnect',
      'server-response-time',
      'redirects',
      'uses-rel-preload',
      'total-byte-weight',
      'dom-size',
      'critical-request-chains',
      'largest-contentful-paint-element',
      'layout-shift-elements',
      'long-tasks',
    ]

    for (const auditId of opportunityAudits) {
      const audit = audits[auditId]
      if (!audit) continue

      // Only include audits that are not passing (score < 1)
      // and have a meaningful numeric value
      if (audit.score !== null && audit.score >= 0.9) continue

      const opportunity: PageSpeedOpportunity = {
        id: audit.id,
        title: audit.title,
        description: audit.description,
        score: audit.score,
        numericValue: audit.numericValue,
        numericUnit: audit.numericUnit,
        displayValue: audit.displayValue,
      }

      // Format savings if available
      if (audit.details?.overallSavingsMs) {
        opportunity.savings = formatMs(audit.details.overallSavingsMs)
      } else if (audit.details?.overallSavingsBytes) {
        opportunity.savings = formatBytes(audit.details.overallSavingsBytes)
      } else if (audit.numericValue && audit.numericUnit === 'millisecond') {
        opportunity.savings = formatMs(audit.numericValue)
      }

      opportunities.push(opportunity)
    }

    // Sort by score (lowest first = most important to fix)
    opportunities.sort((a, b) => (a.score ?? 0) - (b.score ?? 0))

    return opportunities
  } catch (e) {
    console.error('Failed to parse PageSpeed raw data:', e)
    return []
  }
}

/**
 * Format milliseconds for display
 */
function formatMs(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

/**
 * Format bytes for display
 */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
